import { AI_MODELS } from './ai';
import type { AiFilledMark, AiFoundMark, CommentItem, Ref, StageColor, StageKind, ValueSource } from './types';

// Shapes the record sheet (a person or a company) reads from the record area's API, plus the small pure helpers
// both sides share: tab names, call outcomes, multipart part planning, mention parsing and history labels.

export type RecordKind = 'person' | 'company';

export const RECORD_TABS = ['timeline', 'comments', 'history', 'files'] as const;
export type RecordTab = (typeof RECORD_TABS)[number];

/** Maps the tab in the address to one of the sheet's tabs. Older or broader names land on the timeline. */
export function recordTab(tab?: string | null): RecordTab {
  return (RECORD_TABS as readonly string[]).includes(tab ?? '') ? (tab as RecordTab) : 'timeline';
}

export type RecordStatus = 'live' | 'bin' | 'merged';

export interface SheetDeal {
  id: string;
  name: string;
  /** Numeric string, as stored. */
  value: string;
  currency: string;
  stage: { id: string; name: string; color: StageColor; kind: StageKind } | null;
  owner: Ref | null;
  personId: string | null;
  companyId: string | null;
  nextStep: string | null;
}

export interface SheetCounts {
  timeline: number;
  comments: number;
  history: number;
  files: number;
  filesBytes: number;
}

export interface SheetMate {
  id: string;
  name: string;
  hue: number;
  title: string | null;
  lastContactedAt: string | null;
  tags: string[];
}

interface SheetBase {
  id: string;
  name: string;
  hue: number;
  status: RecordStatus;
  /** Where a merged record went. */
  mergedInto: { id: string; name: string } | null;
  deletedAt: string | null;
  deletedBy: Ref | null;
  owner: Ref | null;
  tags: string[];
  source: string | null;
  sourceDetail: string | null;
  custom: Record<string, unknown>;
  createdAt: string;
  createdBy: Ref | null;
  lastContactedAt: string | null;
  /** Open deals with this person, or at this company. */
  openDeals: SheetDeal[];
  counts: SheetCounts;
  /** Live records that look like this one, for the "Possible duplicate" flag. */
  duplicates: { id: string; name: string }[];
}

export interface PersonEmail {
  email: string;
  primary: boolean;
}

export interface PersonSheet extends SheetBase {
  type: 'person';
  title: string | null;
  phone: string | null;
  linkedin: string | null;
  location: string | null;
  /** Main address first. */
  emails: PersonEmail[];
  doNotEmail: boolean;
  doNotEmailReason: string | null;
  /** Set when the person unsubscribed from list emails through the link in one. */
  unsubscribedAt: string | null;
  company: {
    id: string;
    name: string;
    hue: number;
    logo: string | null;
    industry: string | null;
    staff: number | null;
    location: string | null;
    domain: string | null;
    market: string | null;
    /** The company's gaps, for the ones the team fills. */
    lacks: string[];
  } | null;
  /** Other people at the same company. */
  mates: SheetMate[];
  /** Set when the AI Finder found this person: the model and the pages it found them on. */
  aiFound: AiFoundMark | null;
  aiFilled: Record<string, AiFilledMark>;
}

export interface CompanySheet extends SheetBase {
  type: 'company';
  /** The logo from the company's website, as a logo id. */
  logo: string | null;
  domain: string | null;
  website: string | null;
  industry: string | null;
  staff: number | null;
  founded: number | null;
  location: string | null;
  linkedin: string | null;
  phone: string | null;
  description: string | null;
  /** What they do, in a line. */
  summary: string | null;
  market: string | null;
  /** Who they sell to. */
  clients: string | null;
  strengths: string[];
  /** Capabilities they lack. */
  lacks: string[];
  /** The team's angle of approach. */
  angle: string | null;
  aiFilled: Record<string, AiFilledMark>;
  people: SheetMate[];
  /** 'customer' once a deal was won; 'lost' when every closed deal was lost and nothing is open. */
  standing: 'customer' | 'lost' | null;
  lastActivityAt: string | null;
}

export type RecordSheet = PersonSheet | CompanySheet;

/* ---------- notes and calls ---------- */

export const CALL_OUTCOMES = [
  { id: 'connected', label: 'Connected', phrase: 'connected' },
  { id: 'voicemail', label: 'Left a voicemail', phrase: 'left a voicemail' },
  { id: 'no_answer', label: 'No answer', phrase: 'no answer' },
] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number]['id'];

export function outcomePhrase(outcome: string | null | undefined): string | null {
  return CALL_OUTCOMES.find((o) => o.id === outcome)?.phrase ?? null;
}

export interface NoteItem {
  id: string;
  kind: 'note' | 'call';
  body: string;
  callOutcome: CallOutcome | null;
  callDurationMin: number | null;
  callDirection: 'out' | 'in' | null;
  occurredAt: string;
  author: Ref | null;
  person: { id: string; name: string } | null;
  company: { id: string; name: string } | null;
  deal: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

/* ---------- timeline ---------- */

export interface EmailSummary {
  threadId: string;
  emailId: string;
  subject: string;
  snippet: string;
  direction: 'in' | 'out';
  fromName: string | null;
  fromAddr: string;
  to: { email: string; name?: string | null }[];
  via: string;
  sentBy: Ref | null;
  messageCount: number;
  openCount: number;
  trackOpens: boolean;
  lastOpenedAt: string | null;
  attachments: { name: string; size: number; mime: string | null }[];
  person: { id: string; name: string } | null;
}

export interface MeetingItem {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  location: string | null;
  meetUrl: string | null;
  htmlLink: string | null;
  attendees: { email: string; name: string | null; hue: number | null }[];
  status: string;
}

export interface FileItem {
  id: string;
  name: string;
  size: number;
  mime: string | null;
  status: 'uploading' | 'ready' | 'failed';
  uploadedBy: Ref | null;
  createdAt: string;
  person: { id: string; name: string } | null;
  company: { id: string; name: string } | null;
}

export interface DealEvent {
  kind: 'deal_created' | 'deal_moved' | 'deal_won' | 'deal_lost';
  deal: { id: string; name: string } | null;
  value: string | null;
  currency: string | null;
  from: string | null;
  to: string | null;
  fromColor: StageColor | null;
  toColor: StageColor | null;
  reason: string | null;
}

export interface FollowupEvent {
  id: string;
  title: string;
  dueAt: string;
  hasTime: boolean;
  owner: Ref | null;
  doneAt: string | null;
}

export type TimelineItem =
  | { kind: 'note'; id: string; at: string; note: NoteItem }
  | { kind: 'email'; id: string; at: string; email: EmailSummary }
  | { kind: 'meeting'; id: string; at: string; meeting: MeetingItem }
  | { kind: 'comment'; id: string; at: string; comment: CommentItem }
  | { kind: 'file'; id: string; at: string; file: FileItem; by: Ref | null }
  | { kind: 'deal'; id: string; at: string; event: DealEvent; by: Ref | null }
  | { kind: 'followup'; id: string; at: string; followup: FollowupEvent; by: Ref | null }
  | { kind: 'created'; id: string; at: string; by: Ref | null; source: string | null; sourceDetail: string | null }
  | { kind: 'ai'; id: string; at: string; model: string; fields: string[] };

export interface TimelinePage {
  items: TimelineItem[];
  /** Pass as `before` for the next, older page. */
  nextBefore: string | null;
  /** Upcoming meetings are listed at the top regardless of paging. */
  upcoming: TimelineItem[];
}

/* ---------- files ---------- */

export interface FilesStorage {
  configured: boolean;
  bucket: string | null;
  region: string | null;
  /** Who can connect storage when it is missing. */
  admin: { id: string; name: string } | null;
}

export interface FilesResponse {
  storage: FilesStorage;
  items: FileItem[];
}

export interface UploadStart {
  file: FileItem;
  uploadId: string;
  partSize: number;
  partCount: number;
}

/** S3 allows at most 10,000 parts of at least 5 MiB, and objects up to 5 TiB. */
export const MAX_PARTS = 10_000;
export const MIN_PART = 8 * 1024 * 1024;
export const MAX_FILE_BYTES = 5 * 1024 ** 4;

/** Picks a part size that keeps a file of any size within S3's part limit, in whole MiB. */
export function planParts(size: number): { partSize: number; partCount: number } {
  const mib = 1024 * 1024;
  const needed = Math.ceil(size / (MAX_PARTS - 100));
  const partSize = Math.max(MIN_PART, Math.ceil(needed / mib) * mib);
  const partCount = Math.max(1, Math.ceil(size / partSize));
  return { partSize, partCount };
}

/** The short type label a file shows in its tile ("PDF", "MP4"), and the colour family the tile uses. */
export function fileType(name: string, mime?: string | null): { label: string; tone: 'pdf' | 'mp4' | 'fig' | 'zip' | 'xlsx' | 'doc' } {
  const ext = (name.includes('.') ? name.split('.').pop()! : '').toLowerCase().slice(0, 4);
  const label = (ext || (mime?.split('/')[1] ?? 'file')).toUpperCase().slice(0, 4);
  const m = mime ?? '';
  if (ext === 'pdf' || m === 'application/pdf') return { label, tone: 'pdf' };
  if (/^(mp4|mov|avi|mkv|webm|mp3|wav|m4a)$/.test(ext) || m.startsWith('video/') || m.startsWith('audio/')) return { label, tone: 'mp4' };
  if (/^(fig|sketc|psd|ai|png|jpg|jpeg|gif|svg|webp|heic)$/.test(ext) || m.startsWith('image/')) return { label, tone: 'fig' };
  if (/^(zip|rar|7z|tar|gz|tgz)$/.test(ext)) return { label, tone: 'zip' };
  if (/^(xlsx|xls|csv|numb|ods)$/.test(ext)) return { label, tone: 'xlsx' };
  return { label, tone: 'doc' };
}

/* ---------- mentions ---------- */

/** The "@name" being typed just before the caret, if any. */
export function mentionQuery(beforeCaret: string): { query: string; start: number } | null {
  const m = /(^|[^\wÀ-ÿ@])@([\wÀ-ÿ]*)$/.exec(beforeCaret);
  if (!m) return null;
  return { query: m[2]!, start: beforeCaret.length - m[2]!.length - 1 };
}

const HONORIFIC = /^(dr|prof|mr|mrs|ms|mx|sir|dame)\.?$/i;

/** The name to greet someone by: the first word, skipping a title such as "Dr." when a name follows. */
export function firstName(name: string): string {
  const words = name.trim().split(/\s+/);
  const first = words.length > 1 && HONORIFIC.test(words[0]!) ? words[1] : words[0];
  return first || name;
}

/**
 * The teammates a comment mentions, in order of appearance. A mention is "@" plus a first name (or a full name);
 * the longest matching name wins, so "@Maya Lindqvist" and "@Maya" both find Maya.
 */
export function findMentions(body: string, team: { id: string; name: string }[]): string[] {
  const found: string[] = [];
  for (const span of mentionSpans(body, team)) if (span.userId && !found.includes(span.userId)) found.push(span.userId);
  return found;
}

/** Splits a comment into plain text and mention spans for highlighting. */
export function mentionSpans(body: string, team: { id: string; name: string }[]): { text: string; userId: string | null }[] {
  const names = team
    .flatMap((t) => [
      { label: t.name, id: t.id },
      { label: firstName(t.name), id: t.id },
    ])
    .sort((a, b) => b.label.length - a.label.length);
  const out: { text: string; userId: string | null }[] = [];
  let plain = '';
  let i = 0;
  while (i < body.length) {
    const ch = body[i]!;
    const prev = i > 0 ? body[i - 1]! : ' ';
    if (ch === '@' && !/[\wÀ-ÿ]/.test(prev)) {
      const rest = body.slice(i + 1);
      const hit = names.find((n) => rest.toLowerCase().startsWith(n.label.toLowerCase()) && !/[\wÀ-ÿ]/.test(rest[n.label.length] ?? ' '));
      if (hit) {
        if (plain) out.push({ text: plain, userId: null });
        plain = '';
        out.push({ text: `@${rest.slice(0, hit.label.length)}`, userId: hit.id });
        i += 1 + hit.label.length;
        continue;
      }
    }
    plain += ch;
    i += 1;
  }
  if (plain) out.push({ text: plain, userId: null });
  return out;
}

/* ---------- history ---------- */

export interface HistoryRow {
  id: string;
  at: string;
  actor: Ref | null;
  /**
   * True when the value (or, on "Created", the record) came from the AI; the note names the model. `actor` is who
   * accepted it, or null when the AI saved it without anyone.
   */
  byAi: boolean;
  label: string;
  note: string | null;
  /** Where the AI found the value, or the pages it read for a record it found. */
  sources: ValueSource[];
  oldValue: string | null;
  newValue: string | null;
  /** 'date' when both values are calendar dates (YYYY-MM-DD) the browser formats. */
  valueType: 'text' | 'date';
  /** Rows saved in one go share a group. */
  group: string;
}

export interface HistoryResponse {
  rows: HistoryRow[];
  total: number;
}

export const PERSON_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  title: 'Job title',
  company: 'Company',
  emails: 'Email addresses',
  phone: 'Phone',
  linkedin: 'LinkedIn',
  location: 'Location',
  owner: 'Owner',
  tags: 'Tags',
  source: 'Source',
  sourceDetail: 'Source detail',
  doNotEmail: 'Do not email',
  doNotEmailReason: 'Do not email reason',
};

export const COMPANY_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  domain: 'Domain',
  website: 'Website',
  industry: 'Industry',
  staff: 'Staff count',
  founded: 'Founded',
  location: 'Location',
  linkedin: 'LinkedIn',
  phone: 'Phone',
  description: 'Description',
  logo: 'Logo',
  summary: 'What they do',
  market: 'Market',
  clients: 'Sells to',
  strengths: 'Strengths',
  lacks: 'Gaps',
  angle: 'Our angle',
  owner: 'Owner',
  tags: 'Tags',
  source: 'Source',
  sourceDetail: 'Source detail',
};

/** Fields the AI can fill on a company, in the words the sheet uses for them in a sentence. */
export const AI_FIELD_WORDS: Record<string, string> = {
  domain: 'domain',
  website: 'website',
  industry: 'industry',
  staff: 'staff count',
  founded: 'founded year',
  location: 'location',
  linkedin: 'LinkedIn page',
  phone: 'phone number',
  description: 'description',
  summary: 'what they do',
  market: 'market',
  clients: 'who they sell to',
  strengths: 'strengths',
  lacks: 'gaps',
};

/** Fields the AI can fill on a person, in the words the sheet uses for them in a sentence. */
export const PERSON_AI_FIELD_WORDS: Record<string, string> = {
  title: 'job title',
  linkedin: 'LinkedIn profile',
  location: 'location',
  phone: 'phone number',
};

/** A field the AI filled, as a sentence names it: "staff count", "LinkedIn profile". */
export function aiFieldWord(type: RecordKind, field: string): string {
  return (type === 'person' ? PERSON_AI_FIELD_WORDS : AI_FIELD_WORDS)[field] ?? field;
}

/** The display name of an AI model, from its id or its name. */
export function modelName(model: string | null | undefined): string {
  if (!model) return 'AI';
  return AI_MODELS.find((m) => m.id === model)?.name ?? model;
}

/** "industry, staff count and founded year". */
export function listWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}
